import { useState, useEffect, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { toast } from 'sonner';
import { useImportConfigurationStore } from '../../stores/import-configuration-store';
import { useLanguageManagementStore } from '../../stores/language-management-store';
import {
  ColumnMappingRequest,
  validateColumnMapping,
  columnNumberToLetter,
} from '../../types/import-configuration-types';
import { LANGUAGE_NAMES } from '../../types/translation-types';

interface FormData {
  languageCode: string;
  columnNumber: number;
}

export function ColumnMappingDialog() {
  const {
    sheetMappings,
    columnFormDialogOpen,
    columnFormSheetId,
    editingColumnMapping,
    isSubmitting,
    submitError,
    saveColumnMapping,
    closeColumnFormDialog,
    clearSubmitError,
  } = useImportConfigurationStore();

  const { languages, fetchLanguages } = useLanguageManagementStore();

  const isEditMode = editingColumnMapping !== null;

  const [formData, setFormData] = useState<FormData>({
    languageCode: '',
    columnNumber: 3, // Default to column C (after Key=A, Desc=B)
  });

  const [validationError, setValidationError] = useState<string | null>(null);
  const [useCustomLanguage, setUseCustomLanguage] = useState(false);

  // Get the current sheet mapping
  const currentSheetMapping = columnFormSheetId
    ? sheetMappings.find(m => m.mappingId === columnFormSheetId)
    : null;

  // Fetch available languages on mount
  useEffect(() => {
    if (languages.length === 0) {
      fetchLanguages();
    }
  }, [languages.length, fetchLanguages]);

  // Initialize form when dialog opens
  useEffect(() => {
    if (columnFormDialogOpen) {
      clearSubmitError();
      setValidationError(null);

      if (editingColumnMapping) {
        setFormData({
          languageCode: editingColumnMapping.languageCode,
          columnNumber: editingColumnMapping.columnNumber,
        });
        // Check if language code is in our list
        const isKnownLanguage =
          languages.some(l => l.languageCode.toLowerCase() === editingColumnMapping.languageCode.toLowerCase()) ||
          Object.keys(LANGUAGE_NAMES).includes(editingColumnMapping.languageCode.toLowerCase());
        setUseCustomLanguage(!isKnownLanguage);
      } else {
        // Find the next available column number
        const usedColumns = new Set<number>();
        if (currentSheetMapping) {
          usedColumns.add(currentSheetMapping.keyColumnNumber);
          if (currentSheetMapping.descriptionColumnNumber) {
            usedColumns.add(currentSheetMapping.descriptionColumnNumber);
          }
          currentSheetMapping.columnMappings?.forEach(cm => usedColumns.add(cm.columnNumber));
        }

        // Find first available column starting from 3
        let nextColumn = 3;
        while (usedColumns.has(nextColumn)) {
          nextColumn++;
        }

        setFormData({
          languageCode: '',
          columnNumber: nextColumn,
        });
        setUseCustomLanguage(false);
      }
    }
  }, [
    columnFormDialogOpen,
    editingColumnMapping,
    currentSheetMapping,
    clearSubmitError,
    languages,
  ]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!columnFormSheetId || !currentSheetMapping) {
        setValidationError('No sheet mapping selected');
        return;
      }

      // Validate
      const request: ColumnMappingRequest = {
        columnMappingId: editingColumnMapping?.columnMappingId,
        sheetMappingId: columnFormSheetId,
        languageCode: formData.languageCode.trim().toLowerCase(),
        columnNumber: formData.columnNumber,
      };

      const error = validateColumnMapping(
        request,
        currentSheetMapping.columnMappings || [],
        currentSheetMapping.keyColumnNumber,
        currentSheetMapping.descriptionColumnNumber,
        editingColumnMapping?.columnMappingId
      );

      if (error) {
        setValidationError(error);
        return;
      }

      setValidationError(null);

      try {
        await saveColumnMapping(request);
        toast.success(
          isEditMode
            ? `Column mapping for "${formData.languageCode}" updated`
            : `Column mapping for "${formData.languageCode}" created`
        );
      } catch {
        // Error is handled in store
      }
    },
    [
      formData,
      columnFormSheetId,
      currentSheetMapping,
      editingColumnMapping,
      saveColumnMapping,
      isEditMode,
    ]
  );

  // Build language options from stored languages or fallback to LANGUAGE_NAMES
  const languageOptions = languages.length > 0
    ? languages.map(l => ({
        code: l.languageCode,
        name: l.languageName,
      }))
    : Object.entries(LANGUAGE_NAMES).map(([code, name]) => ({ code, name }));

  return (
    <Dialog open={columnFormDialogOpen} onOpenChange={closeColumnFormDialog}>
      <DialogContent className="sm:max-w-[450px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? 'Edit Column Mapping' : 'Add Column Mapping'}
            </DialogTitle>
            <DialogDescription>
              Map an Excel column to a language for importing translations.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Validation error */}
            {validationError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{validationError}</AlertDescription>
              </Alert>
            )}

            {/* API error */}
            {submitError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            )}

            {/* Language Code */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="languageCode">
                  Language <span className="text-red-500">*</span>
                </Label>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => setUseCustomLanguage(!useCustomLanguage)}
                >
                  {useCustomLanguage ? 'Select from list' : 'Enter custom code'}
                </Button>
              </div>

              {useCustomLanguage ? (
                <Input
                  id="languageCode"
                  value={formData.languageCode}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, languageCode: e.target.value }))
                  }
                  placeholder="e.g., en, he, de"
                  disabled={isSubmitting}
                  maxLength={10}
                />
              ) : (
                <Select
                  value={formData.languageCode}
                  onValueChange={value =>
                    setFormData(prev => ({ ...prev, languageCode: value }))
                  }
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="languageCode">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    {languageOptions.map(opt => (
                      <SelectItem key={opt.code} value={opt.code}>
                        {opt.name} ({opt.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-muted-foreground">
                The language code for translations in this column
              </p>
            </div>

            {/* Column Number */}
            <div className="space-y-2">
              <Label htmlFor="columnNumber">
                Column Number <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="columnNumber"
                  type="number"
                  min={1}
                  className="w-24"
                  value={formData.columnNumber}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, columnNumber: Number(e.target.value) }))
                  }
                  disabled={isSubmitting}
                />
                <span className="text-lg font-mono bg-gray-100 px-3 py-1 rounded">
                  = Column {columnNumberToLetter(formData.columnNumber || 1)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Excel column number (1 = A, 2 = B, 3 = C, etc.)
              </p>
            </div>

            {/* Info about used columns */}
            {currentSheetMapping && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-800">
                <strong>Reserved columns:</strong>
                <ul className="list-disc list-inside mt-1">
                  <li>
                    Column {currentSheetMapping.keyColumnNumber} (
                    {columnNumberToLetter(currentSheetMapping.keyColumnNumber)}) - Translation Key
                  </li>
                  {currentSheetMapping.descriptionColumnNumber && (
                    <li>
                      Column {currentSheetMapping.descriptionColumnNumber} (
                      {columnNumberToLetter(currentSheetMapping.descriptionColumnNumber)}) -
                      Description
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeColumnFormDialog}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
